import { ApiError } from "@/lib/apiClient";
import {
  MarketingAutomation,
  CreateMarketingAutomationDto,
  UpdateMarketingAutomationDto,
  FetchMarketingAutomationsOptions,
  MarketingAutomationFilters,
} from "@/types/models/marketing";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface MarketingAutomationsState {
  items: MarketingAutomation[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  currentAutomation: MarketingAutomation | null;
  automationLoading: boolean;
  automationError: ApiError | null;

  searchResults: MarketingAutomation[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
  };

  filters: MarketingAutomationFilters;
  fetchOptions: FetchMarketingAutomationsOptions;
}

const initialState: MarketingAutomationsState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  currentAutomation: null,
  automationLoading: false,
  automationError: null,

  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  formState: {
    loading: false,
    error: null,
    success: false,
  },

  filters: {},
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
};

const marketingAutomationSlice = createSlice({
  name: "marketingAutomations",
  initialState,
  reducers: {
    fetchMarketingAutomationsRequest: (
      state,
      action: PayloadAction<FetchMarketingAutomationsOptions>,
    ) => {
      state.loading = true;
      state.error = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchMarketingAutomationsSuccess: (
      state,
      action: PayloadAction<{ data: MarketingAutomation[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchMarketingAutomationsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    searchMarketingAutomationsRequest: (
      state,
      _action: PayloadAction<{
        query: string;
        options?: FetchMarketingAutomationsOptions;
      }>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchMarketingAutomationsSuccess: (
      state,
      action: PayloadAction<{ data: MarketingAutomation[]; pagination: any }>,
    ) => {
      state.searchLoading = false;
      state.searchResults = action.payload.data;
      state.searchPagination = action.payload.pagination;
    },
    searchMarketingAutomationsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.searchLoading = false;
      state.searchError = action.payload;
    },

    fetchMarketingAutomationDetailsRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.automationLoading = true;
      state.automationError = null;
    },
    fetchMarketingAutomationDetailsSuccess: (
      state,
      action: PayloadAction<MarketingAutomation>,
    ) => {
      state.automationLoading = false;
      state.currentAutomation = action.payload;
    },
    fetchMarketingAutomationDetailsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.automationLoading = false;
      state.automationError = action.payload;
    },

    createMarketingAutomationRequest: (
      state,
      _action: PayloadAction<CreateMarketingAutomationDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createMarketingAutomationSuccess: (
      state,
      action: PayloadAction<MarketingAutomation>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = [action.payload, ...state.items];
    },
    createMarketingAutomationFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    updateMarketingAutomationRequest: (
      state,
      _action: PayloadAction<{
        id: string;
        data: UpdateMarketingAutomationDto;
      }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateMarketingAutomationSuccess: (
      state,
      action: PayloadAction<MarketingAutomation>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.currentAutomation = action.payload;

      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateMarketingAutomationFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    deleteMarketingAutomationRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteMarketingAutomationSuccess: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
    },
    deleteMarketingAutomationFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    setFilters: (state, action: PayloadAction<MarketingAutomationFilters>) => {
      state.filters = action.payload;
    },
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentAutomation: (state) => {
      state.currentAutomation = null;
      state.automationError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchMarketingAutomationsRequest,
  fetchMarketingAutomationsSuccess,
  fetchMarketingAutomationsFailure,
  searchMarketingAutomationsRequest,
  searchMarketingAutomationsSuccess,
  searchMarketingAutomationsFailure,
  fetchMarketingAutomationDetailsRequest,
  fetchMarketingAutomationDetailsSuccess,
  fetchMarketingAutomationDetailsFailure,
  createMarketingAutomationRequest,
  createMarketingAutomationSuccess,
  createMarketingAutomationFailure,
  updateMarketingAutomationRequest,
  updateMarketingAutomationSuccess,
  updateMarketingAutomationFailure,
  deleteMarketingAutomationRequest,
  deleteMarketingAutomationSuccess,
  deleteMarketingAutomationFailure,
  setFilters,
  resetFormState,
  clearCurrentAutomation,
} = marketingAutomationSlice.actions;

export default marketingAutomationSlice.reducer;
